import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { oids } from "@/configs/Settings";
import { Assignment } from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { QuestionnaireProtocolLinkChips } from "@/components/common/QuestionnaireProtocolLinkChips";
import { useOrganizationProtocolQuestionnaireIndex } from "@/services/fhir/protocol/hooks/useOrganizationProtocolQuestionnaireIndex";
import type { QuestionnairePayload } from "src-next/modules/questionnaire-builder/form-builder/types";
import { useQuestionnaireManagement } from "src-next/modules/questionnaire-builder/questionnaire-management/hooks";
import { QUESTIONNAIRE_UNKNOWN_STATUS_TOOLTIP } from "src-next/modules/questionnaire-builder/questionnaire-management/utils/questionnaireManagementConstants";
import { useQuestionnaireTaskSend } from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import { isQuestionnaireSelectableForClinicalUse } from "@/utils/questionnaireClinicalSelection";
import {
  getAllowedUsageTypesForScheduleQuestionnaireSend,
  isQuestionnaireAllowedForScheduleSend,
} from "@/utils/questionnaireUsageTabPolicy";

type UsageTabValue = "all" | QuestionnairePayload["usageType"];

const USAGE_TABS: { value: UsageTabValue; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "triage", label: "Triagem" },
  { value: "preConsultation", label: "Pré-consulta" },
  { value: "postConsultation", label: "Pós-consulta" },
  { value: "attendance", label: "Acompanhamento" },
];

const STANDARD_USAGE: QuestionnairePayload["usageType"][] = [
  "triage",
  "preConsultation",
  "postConsultation",
  "attendance",
];

function matchesUsageTab(
  questionnaire: QuestionnairePayload,
  tab: UsageTabValue,
): boolean {
  if (tab === "all") return true;
  return questionnaire.usageType === tab;
}

function a11yProps(index: number) {
  return {
    id: `send-q-tab-${index}`,
    "aria-controls": `send-q-tabpanel-${index}`,
  };
}

export interface SendQuestionnairesDialogProps {
  open: boolean;
  onClose: () => void;
  /** Referência FHIR completa do paciente, ex. Patient/{id} */
  patientReference: string | null;
  /**
   * Status FHIR do Appointment. Quando omitido, mantém o comportamento anterior (todas as abas).
   */
  appointmentStatus?: string;
  /**
   * `Appointment/{id}` ou id lógico (FHIR) quando o envio estiver vinculado a este agendamento.
   */
  appointmentReference?: string;
  /**
   * Atendimento iniciado para este agendamento (ex. `inProgress === appointment.id` no contexto clínico).
   * Libera pós-consulta na fila antes de `fulfilled`.
   */
  hasActiveEncounter?: boolean;
}

interface FormValues {
  /** IDs dos Questionnaire selecionados para envio. */
  selectedQuestionnaireIds: string[];
}

/**
 * Dialog: lista questionários da organização (src-next), filtra por abas de uso,
 * seleção múltipla com react-hook-form e cria uma Task por item no workflow FHIR.
 */
export const SendQuestionnairesDialog = memo(function SendQuestionnairesDialog({
  open,
  onClose,
  patientReference,
  appointmentStatus,
  appointmentReference,
  hasActiveEncounter,
}: SendQuestionnairesDialogProps) {
  const { access, user } = useSessionContext();
  const { organizationId } = useOrganizationContext();
  const [usageTab, setUsageTab] = useState<UsageTabValue>("all");

  const scheduleAllowedUsage = useMemo(() => {
    if (appointmentStatus === undefined || appointmentStatus === "") {
      return null;
    }
    return getAllowedUsageTypesForScheduleQuestionnaireSend(
      appointmentStatus,
      { hasActiveEncounter },
    );
  }, [appointmentStatus, hasActiveEncounter]);

  const visibleUsageTabs = useMemo(() => {
    if (scheduleAllowedUsage === null) {
      return USAGE_TABS;
    }
    if (scheduleAllowedUsage.length === 0) {
      return [];
    }
    const tabs: { value: UsageTabValue; label: string }[] = [
      { value: "all", label: "Todos" },
    ];
    for (const row of USAGE_TABS) {
      if (row.value === "all") continue;
      if (
        STANDARD_USAGE.includes(row.value as QuestionnairePayload["usageType"]) &&
        scheduleAllowedUsage.includes(
          row.value as QuestionnairePayload["usageType"],
        )
      ) {
        tabs.push(row);
      }
    }
    return tabs;
  }, [scheduleAllowedUsage]);

  const { status: protocolIndexStatus, getProtocolNamesForQuestionnaire } =
    useOrganizationProtocolQuestionnaireIndex({ enabled: open });

  const { questionnaires, isLoading, errorMessage } =
    useQuestionnaireManagement({
      organizationId: `${oids.cnpj}-${organizationId}`,
    });

  const { sendQuestionnaireTasks, isSending, sendError, resetSendState } =
    useQuestionnaireTaskSend();

  const { watch, getValues, setValue, handleSubmit, reset } =
    useForm<FormValues>({
      defaultValues: { selectedQuestionnaireIds: [] },
    });

  const [confirmSendOpen, setConfirmSendOpen] = useState(false);

  const selectedQuestionnaireIds = watch("selectedQuestionnaireIds");

  const filteredQuestionnaires = useMemo(() => {
    return questionnaires.filter((q) => {
      if (!isQuestionnaireSelectableForClinicalUse(q)) return false;
      if (
        scheduleAllowedUsage !== null &&
        !isQuestionnaireAllowedForScheduleSend(q, scheduleAllowedUsage)
      ) {
        return false;
      }
      return matchesUsageTab(q, usageTab);
    });
  }, [questionnaires, scheduleAllowedUsage, usageTab]);

  useEffect(() => {
    if (!open) return;
    const values = visibleUsageTabs.map((t) => t.value);
    if (values.length === 0) return;
    if (!values.includes(usageTab)) {
      setUsageTab(values[0]!);
    }
  }, [open, visibleUsageTabs, usageTab]);

  const practitionerRoleReference = useMemo(() => {
    const username = user?.username;
    if (!access?.organizationId || typeof username !== "string") return "";
    return `PractitionerRole/${access.organizationId}-${oids.cpf}-${username}`;
  }, [access?.organizationId, user?.username]);

  const scheduleAllowsSend =
    scheduleAllowedUsage === null || scheduleAllowedUsage.length > 0;

  const canSend =
    !!patientReference &&
    !!access?.organizationId &&
    access.type === "professional" &&
    !!practitionerRoleReference &&
    scheduleAllowsSend;

  const toggleQuestionnaireId = useCallback(
    (questionnaireId: string) => {
      const current = getValues("selectedQuestionnaireIds");
      const next = current.includes(questionnaireId)
        ? current.filter((id) => id !== questionnaireId)
        : [...current, questionnaireId];
      setValue("selectedQuestionnaireIds", next, { shouldValidate: true });
    },
    [getValues, setValue],
  );

  const handleClose = useCallback(() => {
    setConfirmSendOpen(false);
    reset({ selectedQuestionnaireIds: [] });
    resetSendState();
    setUsageTab("all");
    onClose();
  }, [onClose, reset, resetSendState]);

  const openConfirmSendDialog = handleSubmit((values) => {
    if (!patientReference || !organizationId || !practitionerRoleReference) {
      toast.error(
        "Dados de organização, profissional ou paciente incompletos.",
      );
      return;
    }
    if (!values.selectedQuestionnaireIds.length) {
      toast.error("Selecione ao menos um questionário.");
      return;
    }
    setConfirmSendOpen(true);
  });

  const performSendQuestionnaires = useCallback(async () => {
    const values = getValues();
    if (!patientReference || !organizationId || !practitionerRoleReference) {
      toast.error(
        "Dados de organização, profissional ou paciente incompletos.",
      );
      return;
    }
    if (!values.selectedQuestionnaireIds.length) {
      toast.error("Selecione ao menos um questionário.");
      return;
    }

    const selectedPayloads = questionnaires
      .filter((q) => q.id && values.selectedQuestionnaireIds.includes(q.id))
      .filter(isQuestionnaireSelectableForClinicalUse);

    if (!selectedPayloads.length) {
      toast.error(
        "Nenhum questionário válido para envio (rascunhos não são enviados).",
      );
      return;
    }

    try {
      await sendQuestionnaireTasks({
        organizationId,
        practitionerRoleReference,
        patientReference,
        ...(String(appointmentReference ?? "").trim()
          ? { appointmentReference: String(appointmentReference).trim() }
          : {}),
        questionnaires: selectedPayloads,
      });
      toast.success("Questionários enviados ao paciente.");
      handleClose();
    } catch {
      /* erro já mapeado em sendError / toast opcional */
    }
  }, [
    appointmentReference,
    getValues,
    handleClose,
    organizationId,
    patientReference,
    practitionerRoleReference,
    questionnaires,
    sendQuestionnaireTasks,
  ]);

  const handleMainDialogClose = useCallback(
    (_event: unknown, _reason?: "backdropClick" | "escapeKeyDown") => {
      if (isSending) return;
      handleClose();
    },
    [handleClose, isSending],
  );

  const handleConfirmDialogClose = useCallback(
    (_event: unknown, _reason?: "backdropClick" | "escapeKeyDown") => {
      if (isSending) return;
      setConfirmSendOpen(false);
    },
    [isSending],
  );

  return (
    <>
    <Dialog
      open={open}
      onClose={handleMainDialogClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="send-questionnaires-title"
      disableEscapeKeyDown={isSending}
    >
      <DialogTitle id="send-questionnaires-title">
        Enviar questionários ao paciente
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        {!canSend && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {scheduleAllowedUsage !== null && scheduleAllowedUsage.length === 0
              ? "Envio de questionários não está disponível para este status de consulta (ex.: cancelada)."
              : "Não é possível enviar: verifique paciente, organização e perfil profissional."}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        {sendError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {sendError}
          </Alert>
        )}

        {visibleUsageTabs.length > 0 ? (
          <Tabs
            value={usageTab}
            onChange={(_, value: UsageTabValue) => setUsageTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
          >
            {visibleUsageTabs.map((tab, index) => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        ) : null}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : filteredQuestionnaires.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum questionário nesta categoria.
          </Typography>
        ) : (
          <FormGroup>
            <List dense disablePadding>
              {filteredQuestionnaires.map((questionnaire) => {
                const qid = questionnaire.id;
                if (!qid) return null;
                const checked = selectedQuestionnaireIds.includes(qid);
                return (
                  <ListItem key={qid} disablePadding>
                    <ListItemButton
                      dense
                      onClick={() => toggleQuestionnaireId(qid)}
                    >
                      <ListItemIcon sx={{ minWidth: 42 }}>
                        <Checkbox
                          edge="start"
                          checked={checked}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{
                            "aria-labelledby": `send-q-label-${qid}`,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={`send-q-label-${qid}`}
                        primary={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            component="span"
                          >
                            <Typography component="span" variant="body1">
                              {questionnaire.title}
                            </Typography>
                            {questionnaire.status === "unknown" ? (
                              <Tooltip
                                title={QUESTIONNAIRE_UNKNOWN_STATUS_TOOLTIP}
                                arrow
                              >
                                <ErrorOutlineIcon
                                  sx={{
                                    fontSize: 22,
                                    color: "error.main",
                                    display: "block",
                                  }}
                                  aria-label="Informação sobre versão vista pelos pacientes"
                                />
                              </Tooltip>
                            ) : null}
                          </Stack>
                        }
                        secondaryTypographyProps={{ component: "div" }}
                        secondary={
                          <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                            {questionnaire.description?.trim() ? (
                              <Typography variant="body2" color="text.secondary">
                                {questionnaire.description}
                              </Typography>
                            ) : null}
                            {questionnaire.usageType === "postConsultation" ? (
                              <QuestionnaireProtocolLinkChips
                                protocolNames={getProtocolNamesForQuestionnaire(
                                  qid,
                                )}
                                status={protocolIndexStatus}
                              />
                            ) : null}
                          </Stack>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={isSending}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void openConfirmSendDialog();
          }}
          disabled={!canSend || isSending || isLoading || confirmSendOpen}
          startIcon={<Assignment />}
        >
          Enviar
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={confirmSendOpen}
      onClose={handleConfirmDialogClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="send-questionnaires-confirm-title"
      aria-describedby="send-questionnaires-confirm-desc"
      disableEscapeKeyDown={isSending}
    >
      <DialogTitle id="send-questionnaires-confirm-title">
        Enviar ao paciente
      </DialogTitle>
      <DialogContent>
        <Typography
          id="send-questionnaires-confirm-desc"
          variant="body1"
          color="text.secondary"
          component="div"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            fontSize: { xs: "1rem", sm: "1.0625rem" },
            lineHeight: 1.6,
          }}
        >
          <span>
            Ao confirmar, o(s) questionário(s) escolhido(s) passam a ficar
            disponíveis para o paciente no aplicativo ou no site da Fluxmed.
          </span>
          <span>
            Oriente o paciente a acessar{" "}
            <Link
              href="https://app.fluxmed.com.br"
              target="_blank"
              rel="noopener noreferrer"
            >
              app.fluxmed.com.br
            </Link>{" "}
            e entrar com o login dele para responder.
          </span>
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setConfirmSendOpen(false)}
          color="inherit"
          disabled={isSending}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void performSendQuestionnaires();
          }}
          disabled={isSending}
          startIcon={
            isSending ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
        >
          Ok
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
});
